import { useCallback, useEffect, useRef, useState } from "react";
import { Play, ArrowsOut, ArrowsIn } from "@phosphor-icons/react";

export default function CodeSandbox({ html, css, js }) {
  const iframeRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRunning, setIsRunning] = useState(true);

  const embeddedIframeSrcMatch = (html || "").match(/<iframe[^>]*src=["']([^"']+)["'][^>]*>/i);
  const embeddedIframeSrc = embeddedIframeSrcMatch?.[1] || "";
  const hasStandaloneEmbed = Boolean(embeddedIframeSrc && !(css || "").trim() && !(js || "").trim());

  const updatePreview = useCallback(() => {
    if (!iframeRef.current) return;

    const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    
    // Sanitized content with sandbox restrictions
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: system-ui, sans-serif;
              background: #1a1a24;
              color: #f8f8f8;
              padding: 16px;
              min-height: 100%;
            }
            ${css || ''}
          </style>
        </head>
        <body>
          ${html || '<p style="color: #71717a;">No HTML content</p>'}
          <script>
            // Wrap in try-catch for safety
            try {
              ${js || ''}
            } catch(e) {
              console.error('Sandbox error:', e);
            }
          </script>
        </body>
      </html>
    `;

    doc.open();
    doc.write(content);
    doc.close();
  }, [html, css, js]);

  useEffect(() => {
    if (isRunning) {
      updatePreview();
    }
  }, [isRunning, updatePreview]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`relative ${isExpanded ? 'fixed inset-4 z-50' : ''}`}>
      {/* Controls */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        <button
          onClick={() => setIsRunning(!isRunning)}
          className={`p-2 rounded-lg transition-colors ${
            isRunning 
              ? 'bg-accent/20 text-accent' 
              : 'bg-surface-elevated text-muted-foreground hover:text-foreground'
          }`}
          title={isRunning ? "Pause" : "Run"}
        >
          <Play weight={isRunning ? "fill" : "regular"} className="w-4 h-4" />
        </button>
        <button
          onClick={toggleExpand}
          className="p-2 rounded-lg bg-surface-elevated text-muted-foreground hover:text-foreground transition-colors"
          title={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? (
            <ArrowsIn className="w-4 h-4" />
          ) : (
            <ArrowsOut className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Backdrop for expanded view */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm -z-10"
          onClick={toggleExpand}
        />
      )}

      {/* Preview iframe */}
      <div className={`bg-surface-elevated rounded-b-xl overflow-hidden ${isExpanded ? 'h-full' : ''}`}>
        {hasStandaloneEmbed ? (
          <iframe
            title="Embedded Preview"
            src={embeddedIframeSrc}
            className={`w-full border-0 bg-background ${isExpanded ? 'h-full min-h-[400px]' : 'h-72'}`}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="code-embed-iframe"
          />
        ) : (
          <iframe
            ref={iframeRef}
            title="Code Preview"
            sandbox="allow-scripts allow-forms allow-popups allow-modals allow-presentation"
            className={`w-full border-0 ${isExpanded ? 'h-full min-h-[400px]' : 'h-64'}`}
            data-testid="code-sandbox-iframe"
          />
        )}
      </div>
    </div>
  );
}
